import { useEffect, useMemo, useState } from "react";
import { Modal, Progress, Spin, Tooltip } from "antd";
import {
	CheckCircle2,
	ChevronDown,
	ChevronRight,
	Copy,
	Loader2,
	LifeBuoy,
	RotateCw,
	Sparkles,
	UploadCloud,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { FixingLoader } from "@/components/ui/FixingLoader";
import { cn } from "@/lib/utils";
import type {
	ApplyFixesResponse,
	DuplicateGroup,
	PageIssue,
} from "@/types/technicalAudit";
import { useQueryClient } from "@tanstack/react-query";
import {
	useApplySelectedFixes,
	useBulkGenerateSuggestions,
	useBulkUpdateIssues,
	useDuplicateGroups,
	useRequestManualFix,
} from "@/hooks/useTechnicalAuditIssues";

const TYPE_LABELS: Record<DuplicateGroup["issue_type"], string> = {
	duplicate_title: "Duplicate Title",
	duplicate_meta: "Duplicate Meta Description",
	duplicate_h1: "Duplicate H1",
};

// Terminal statuses — member no longer actionable. Mirrors the backend set.
const TERMINAL = new Set([
	"applied",
	"verified",
	"skipped",
	"rejected",
	"protected",
	"obsolete",
	"manual",
]);

function isGenerating(issue: PageIssue): boolean {
	return (
		issue.generation_status === "queued" ||
		issue.generation_status === "generating"
	);
}

function hasSuggestion(issue: PageIssue): boolean {
	return Boolean(issue.proposed_value || issue.approved_value);
}

// After this many failed generation attempts, retrying is no longer a plan —
// offer the expert-fix ticket escape hatch (mirrors the review-only flow).
const EXPERT_ESCALATION_ATTEMPTS = 3;

/** The backend's classified failure message ("[EMPTY_RESPONSE] The AI provider
 *  returned an EMPTY response…") — surfaced on hover instead of a bare
 *  "Generation failed" that tells the user nothing. */
function generationError(issue: PageIssue): string | null {
	const details = (issue.issue_details ?? {}) as {
		auto_suggest?: { error?: string };
	};
	return details.auto_suggest?.error?.trim() || null;
}

function generationAttempts(issue: PageIssue): number {
	const details = (issue.issue_details ?? {}) as {
		auto_suggest?: { attempts?: number };
	};
	return Number(details.auto_suggest?.attempts ?? 0);
}

/**
 * ``optimisticQueued`` bridges the gap between clicking Generate and the first
 * refetch reflecting the queue: without it, the progress monitor showed
 * "No suggestion yet" for seconds and read as stuck (Jacob QA, 2026-07-08).
 */
function memberState(
	issue: PageIssue,
	optimisticQueued?: Set<string>,
): { label: string; cls: string; generating: boolean } {
	if (isGenerating(issue)) {
		return {
			label: issue.generation_status === "queued" ? "Queued…" : "Generating…",
			cls: "bg-teal-light text-teal-dark border-teal/40",
			generating: true,
		};
	}
	// An explicit (re)queue this session shows "Queued…" even over a stale
	// "failed" — the prune effect drops the id once the backend confirms the
	// new attempt (or it produces a suggestion), so a SECOND real failure
	// still surfaces as failed.
	if (optimisticQueued?.has(String(issue.id)) && !hasSuggestion(issue)) {
		return {
			label: "Queued…",
			cls: "bg-teal-light text-teal-dark border-teal/40",
			generating: true,
		};
	}
	if (issue.generation_status === "failed" && !hasSuggestion(issue)) {
		return {
			label: "Generation failed",
			cls: "bg-destructive/10 text-destructive border-destructive/30",
			generating: false,
		};
	}
	const fs = issue.fix_status;
	if (fs === "applied" || fs === "verified") {
		return {
			label: fs === "verified" ? "Verified" : "Applied",
			cls: "bg-teal-light text-teal-dark border-teal/40",
			generating: false,
		};
	}
	if (TERMINAL.has(fs)) {
		return {
			label: fs === "manual" ? "Expert fix requested" : fs,
			cls: "bg-muted text-muted-foreground border-border",
			generating: false,
		};
	}
	if (fs === "approved") {
		return {
			label: "Approved — ready to apply",
			cls: "bg-coral-light text-coral-dark border-coral/40",
			generating: false,
		};
	}
	if (hasSuggestion(issue)) {
		return {
			label: "Suggestion ready",
			cls: "bg-coral-light text-coral-dark border-coral/40",
			generating: false,
		};
	}
	return {
		label: "No suggestion yet",
		cls: "bg-muted text-muted-foreground border-border",
		generating: false,
	};
}

interface GenerationProgressModalProps {
	open: boolean;
	onClose: () => void;
	group: DuplicateGroup;
	optimisticQueued: Set<string>;
	onRetry: (issueId: string) => void;
	retryPending: boolean;
}

/**
 * Live generation monitor. Reads straight from the (polled) group prop, so
 * status chips update every refetch while any member is still generating —
 * no extra polling of its own.
 */
function GenerationProgressModal({
	open,
	onClose,
	group,
	optimisticQueued,
	onRetry,
	retryPending,
}: GenerationProgressModalProps) {
	const tracked = group.issues.filter((i) => !TERMINAL.has(i.fix_status));
	const ready = tracked.filter(hasSuggestion).length;
	const generating = tracked.filter(
		(i) => memberState(i, optimisticQueued).generating,
	).length;
	// Count each row ONCE: a failed row the user just re-queued shows (and
	// counts) as generating, not failed — "3 generating · 2 failed" out of 3
	// tracked was double-counting (Jacob QA).
	const failed = tracked.filter(
		(i) =>
			i.generation_status === "failed" &&
			!hasSuggestion(i) &&
			!memberState(i, optimisticQueued).generating,
	).length;

	return (
		<Modal
			open={open}
			onCancel={onClose}
			footer={null}
			title={`Generating suggestions — “${group.shared_value ?? ""}”`}
		>
			<p className="mb-3 text-sm text-muted-foreground">
				<span className="font-medium">{ready}</span> of {tracked.length} ready
				{generating > 0 && <> · {generating} generating</>}
				{failed > 0 && (
					<>
						{" "}
						· <span className="text-destructive">{failed} failed</span>
					</>
				)}
				{generating === 0 && failed === 0 && ready === tracked.length && (
					<span className="ml-1 text-teal-dark">— all done, review below.</span>
				)}
			</p>
			<ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
				{tracked.map((issue) => {
					const state = memberState(issue, optimisticQueued);
					const failedRow =
						issue.generation_status === "failed" && !hasSuggestion(issue);
					return (
						<li
							key={String(issue.id)}
							className="flex items-center gap-3 px-3 py-2"
						>
							<span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
								{issue.page_url}
								{failedRow && generationError(issue) && (
									<span className="mt-0.5 block truncate font-sans text-[11px] font-normal text-destructive">
										{generationError(issue)}
									</span>
								)}
							</span>
							{failedRow && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="h-6 gap-1 px-2 text-xs"
									disabled={retryPending}
									onClick={() => onRetry(String(issue.id))}
								>
									<RotateCw className="h-3 w-3" />
									Retry
								</Button>
							)}
							<Badge variant="outline" className={cn("shrink-0", state.cls)}>
								{state.generating && (
									<Loader2 className="mr-1 h-3 w-3 animate-spin" />
								)}
								{state.label}
							</Badge>
						</li>
					);
				})}
			</ul>
		</Modal>
	);
}

/** Which slice of a group's members the user wants to look at. Applied work
 *  stays inside its group (a separate tab would divorce a fixed page from the
 *  duplicate set it was fixed against). */
type StatusFilter = "todo" | "applied" | "all";

const APPLIED = new Set(["applied", "verified"]);

function matchesFilter(issue: PageIssue, filter: StatusFilter): boolean {
	if (filter === "all") return true;
	if (filter === "applied") return APPLIED.has(issue.fix_status);
	return !APPLIED.has(issue.fix_status); // "todo"
}

type ApplyStep =
	| "closed"
	| "confirm"
	| "applying"
	| "background"
	| "lost"
	| "done";

interface ApplyProgressModalProps {
	step: ApplyStep;
	count: number;
	result: ApplyFixesResponse | null;
	/** Live member rows for the issues being applied (from the polled group). */
	targets: PageIssue[];
	onConfirm: () => void;
	onCollapse: () => void;
	onCancelWait: () => void;
	onClose: () => void;
}

/** Live status line for one apply target, derived from polled server truth. */
function applyTargetState(issue: PageIssue): { label: string; cls: string } {
	const fs = issue.fix_status;
	if (fs === "applied" || fs === "verified") {
		return { label: "✓ Applied", cls: "text-teal-dark" };
	}
	if (fs === "failed") {
		return { label: "✗ Failed", cls: "text-destructive" };
	}
	if (fs === "approved") {
		return { label: "Waiting…", cls: "text-muted-foreground" };
	}
	return { label: fs, cls: "text-muted-foreground" };
}

/**
 * Batch-apply flow with an honest progress screen (Jacob QA: the bare confirm
 * button's brief spinner read as "did it even work?"). The apply is ONE
 * long-running request (a live WordPress write per page), so the bar shows
 * time-based progress toward an estimate, never a fake per-page count.
 */
function ApplyProgressModal({
	step,
	count,
	result,
	targets,
	onConfirm,
	onCollapse,
	onCancelWait,
	onClose,
}: ApplyProgressModalProps) {
	const [percent, setPercent] = useState(0);
	// Server truth: fixes commit per page as the batch runs, so the polled
	// group data IS the live activity log.
	const settled = targets.filter(
		(t) => t.fix_status !== "approved" && t.fix_status !== "pending",
	).length;

	useEffect(() => {
		if (step !== "applying" && step !== "lost") {
			setPercent(step === "done" ? 100 : 0);
			return;
		}
		// Blend real per-page completion with a time-based crawl toward 90%;
		// 100 only when the backend answers (or every target settles).
		const startedAt = Date.now();
		const expectedMs = Math.max(8_000, count * 6_000);
		const timer = window.setInterval(() => {
			const elapsed = Date.now() - startedAt;
			const timePct = Math.min(90, Math.round((elapsed / expectedMs) * 100));
			const realPct = count > 0 ? Math.round((settled / count) * 100) : 0;
			setPercent(Math.min(99, Math.max(timePct, realPct)));
		}, 500);
		return () => window.clearInterval(timer);
	}, [step, count, settled]);

	return (
		<Modal
			open={step !== "closed" && step !== "background"}
			onCancel={step === "applying" || step === "lost" ? onCollapse : onClose}
			closable
			maskClosable={false}
			footer={null}
			title={
				step === "done"
					? "Apply finished"
					: step === "lost"
						? "Still applying on the server"
						: `Apply ${count} approved ${count === 1 ? "fix" : "fixes"} to WordPress?`
			}
		>
			{step === "confirm" && (
				<div className="space-y-4">
					<p className="text-sm text-foreground">
						This updates the live pages so each gets its distinct value. Every
						applied fix keeps its own history and can be reverted individually.
					</p>
					<div className="flex justify-end gap-2">
						<Button type="button" variant="outline" onClick={onClose}>
							Cancel
						</Button>
						<Button
							type="button"
							className="gap-1.5 bg-coral text-white hover:bg-coral-hover"
							onClick={onConfirm}
						>
							<UploadCloud className="h-4 w-4" />
							Apply to WordPress
						</Button>
					</div>
				</div>
			)}
			{(step === "applying" || step === "lost") && (
				<div className="space-y-4 py-2">
					<div className="flex justify-center">
						<FixingLoader
							label={
								step === "lost"
									? "Connection to the request was lost — the batch keeps running on the server; statuses below stay live."
									: `Applying ${count} ${count === 1 ? "fix" : "fixes"} to WordPress… (${settled} of ${count} done)`
							}
							size={28}
						/>
					</div>
					<Progress percent={percent} status="active" showInfo={false} />
					{/* Live activity log: fixes commit per page server-side, so the
					    polled statuses ARE the log — no fake ticker. */}
					<ul className="max-h-56 divide-y overflow-y-auto rounded-md border">
						{targets.map((t) => {
							const st = applyTargetState(t);
							return (
								<li
									key={String(t.id)}
									className="flex items-center gap-3 px-3 py-1.5"
								>
									<span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground">
										{t.page_url}
									</span>
									<span className={cn("shrink-0 text-xs font-medium", st.cls)}>
										{st.label}
									</span>
								</li>
							);
						})}
					</ul>
					<div className="flex items-center justify-between gap-2">
						<p className="text-xs text-muted-foreground">
							Each page is a live WordPress write — you can put this in the
							background; it keeps running.
						</p>
						<div className="flex shrink-0 gap-2">
							<Tooltip title="Stop waiting here. WordPress writes already started on the server will still finish — the page statuses keep updating in the group.">
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={onCancelWait}
								>
									Cancel
								</Button>
							</Tooltip>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={onCollapse}
							>
								Continue in background
							</Button>
						</div>
					</div>
				</div>
			)}
			{step === "done" && result && (
				<div className="space-y-3">
					<p className="text-sm">
						<span className="font-medium text-teal-dark">
							{result.applied_count} applied
						</span>
						{result.failed_count > 0 && (
							<span className="ml-2 font-medium text-destructive">
								{result.failed_count} failed
							</span>
						)}
					</p>
					{result.failed_count > 0 && (result.errors?.length ?? 0) > 0 && (
						<ul className="max-h-40 space-y-1 overflow-y-auto rounded-md border bg-destructive/5 p-2 text-xs text-destructive">
							{(result.errors ?? []).slice(0, 8).map((e, i) => (
								<li key={i} className="break-all">
									{typeof e === "string" ? e : JSON.stringify(e)}
								</li>
							))}
						</ul>
					)}
					<p className="text-xs text-muted-foreground">
						Applied pages show as “Applied” in the group and carry their own
						history / revert.
					</p>
					<div className="flex justify-end">
						<Button type="button" variant="outline" onClick={onClose}>
							Close
						</Button>
					</div>
				</div>
			)}
		</Modal>
	);
}

interface GroupCardProps {
	group: DuplicateGroup;
	runId: string;
	statusFilter: StatusFilter;
	onIssueClick: (issue: PageIssue) => void;
}

function GroupCard({ group, runId, statusFilter, onIssueClick }: GroupCardProps) {
	// Collapsed by default — 58-group runs must scan as compact header rows.
	const [expanded, setExpanded] = useState(false);
	const [progressOpen, setProgressOpen] = useState(false);
	// Ids queued THIS session (id -> queuedAt ms), shown as "Queued…" until the
	// poll catches up. TTL-pruned: if the backend never confirms within 45s
	// (e.g. the attempt failed between polls), real state — including
	// "Generation failed" — resurfaces instead of a forever-stuck "Queued…"
	// (Jacob QA round 5).
	const [optimisticQueued, setOptimisticQueued] = useState<Map<string, number>>(
		() => new Map(),
	);
	const queryClient = useQueryClient();
	const [applyStep, setApplyStep] = useState<ApplyStep>("closed");
	const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
	const [applyResult, setApplyResult] = useState<ApplyFixesResponse | null>(
		null,
	);
	// The ids the in-flight batch targets — drives the live activity log.
	const [applyTargetIds, setApplyTargetIds] = useState<string[]>([]);
	// Default selection: every actionable member. Users deselect pages they
	// want to leave alone (e.g. the ONE page that should keep the title).
	const [selected, setSelected] = useState<Set<string>>(
		() => new Set(group.actionable_issue_ids),
	);
	const generate = useBulkGenerateSuggestions();
	const approve = useBulkUpdateIssues();
	const applySelected = useApplySelectedFixes();
	const requestManualFix = useRequestManualFix();

	const byId = useMemo(() => {
		const m = new Map<string, PageIssue>();
		for (const i of group.issues) m.set(String(i.id), i);
		return m;
	}, [group.issues]);

	const selectedIssues = [...selected]
		.map((id) => byId.get(id))
		.filter((i): i is PageIssue => Boolean(i) && !TERMINAL.has(i!.fix_status));

	const optimisticSet = useMemo(
		() => new Set(optimisticQueued.keys()),
		[optimisticQueued],
	);

	// Prune optimistic ids once the backend confirms them (really generating,
	// or a suggestion landed) OR after the 45s TTL — after that, real state
	// (including a fast "failed") wins again.
	useEffect(() => {
		setOptimisticQueued((prev) => {
			if (prev.size === 0) return prev;
			const now = Date.now();
			const next = new Map(prev);
			for (const [id, queuedAt] of prev) {
				const issue = byId.get(id);
				const confirmed =
					issue && (isGenerating(issue) || hasSuggestion(issue));
				if (confirmed || now - queuedAt > 45_000) {
					next.delete(id);
				}
			}
			return next.size === prev.size ? prev : next;
		});
	}, [byId]);

	// While optimistic entries exist, the shared poll may be idle (it keys off
	// REAL generation_status) — keep refetching + TTL-pruning ourselves so the
	// card converges on server truth even if the backend failed between polls.
	useEffect(() => {
		if (optimisticQueued.size === 0) return;
		const timer = window.setInterval(() => {
			queryClient.invalidateQueries({
				queryKey: ["technical-audit", "duplicate-groups"],
			});
			setOptimisticQueued((prev) => {
				const now = Date.now();
				const next = new Map(prev);
				for (const [id, queuedAt] of prev) {
					if (now - queuedAt > 45_000) next.delete(id);
				}
				return next.size === prev.size ? prev : next;
			});
		}, 10_000);
		return () => window.clearInterval(timer);
	}, [optimisticQueued.size, queryClient]);

	const applyInFlight =
		applyStep === "applying" || applyStep === "background" || applyStep === "lost";
	useEffect(() => {
		if (!applyInFlight) return;
		const timer = window.setInterval(() => {
			queryClient.invalidateQueries({
				queryKey: ["technical-audit", "duplicate-groups"],
			});
		}, 10_000);
		return () => window.clearInterval(timer);
	}, [applyInFlight, queryClient]);

	const rowGenerating = (i: PageIssue) =>
		memberState(i, optimisticSet).generating;

	const needGeneration = selectedIssues.filter(
		(i) => !hasSuggestion(i) && !rowGenerating(i),
	);
	// Regenerate targets: un-approved suggestions regenerate directly; APPROVED
	// ones need the explicit "discard N approvals" confirm (Jacob QA: a silently
	// disabled Regenerate on an all-approved group read as broken).
	const regenPending = selectedIssues.filter(
		(i) => hasSuggestion(i) && i.fix_status === "pending" && !rowGenerating(i),
	);
	const regenApproved = selectedIssues.filter(
		(i) => hasSuggestion(i) && i.fix_status === "approved",
	);
	const regenerable = [...regenPending, ...regenApproved];
	const readyToApprove = selectedIssues.filter(
		(i) => hasSuggestion(i) && i.fix_status !== "approved",
	);
	const readyToApply = selectedIssues.filter(
		(i) => i.fix_status === "approved",
	);
	const anyGenerating = group.issues.some(rowGenerating);
	const suggestionsReady = group.issues.filter(hasSuggestion).length;
	const appliedCount = group.issues.filter(
		(i) => i.fix_status === "applied" || i.fix_status === "verified",
	).length;
	const allApplied =
		group.issues.length > 0 && appliedCount === group.issues.length;

	const toggle = (id: string, on: boolean) => {
		setSelected((prev) => {
			const next = new Set(prev);
			if (on) next.add(id);
			else next.delete(id);
			return next;
		});
	};

	const startGeneration = (
		issues: PageIssue[],
		force: boolean,
		includeApproved = false,
	) => {
		const ids = issues.map((i) => String(i.id));
		setOptimisticQueued((prev) => {
			const next = new Map(prev);
			const now = Date.now();
			for (const id of ids) next.set(id, now);
			return next;
		});
		generate.mutate(
			{ issueIds: ids, force, includeApproved },
			{
				// Open the monitor right away so the user watches progress instead
				// of wondering whether the click did anything.
				onSuccess: () => setProgressOpen(true),
				onError: () =>
					setOptimisticQueued((prev) => {
						const next = new Map(prev);
						for (const id of ids) next.delete(id);
						return next;
					}),
			},
		);
	};

	const runApply = () => {
		const ids = readyToApply.map((i) => String(i.id));
		setApplyTargetIds(ids);
		setApplyStep("applying");
		applySelected
			.mutateAsync({ runId, issueIds: ids })
			.then((res) => {
				setApplyResult(res);
				setApplyStep("done");
			})
			.catch(() => {
				// Request-level failure (e.g. client timeout) ≠ batch failure: the
				// server processes per page and keeps going. Switch to the honest
				// "connection lost, still applying" state — the polled statuses
				// carry the truth. (The hook already raised a sticky error.)
				setApplyStep("lost");
			});
	};

	return (
		<div className="rounded-xl border bg-card shadow-sm">
			{/* Collapsible header: group checkbox (always visible, even collapsed —
			    Jacob QA round 7) + the rest of the row toggles expansion. A div,
			    not a button, because a checkbox can't nest inside a button. */}
			<div className="flex w-full items-center gap-2 px-4 py-3">
				<Checkbox
					checked={
						group.actionable_issue_ids.length > 0 &&
						group.actionable_issue_ids.every((id) => selected.has(id))
							? true
							: group.actionable_issue_ids.some((id) => selected.has(id))
								? "indeterminate"
								: false
					}
					disabled={group.actionable_issue_ids.length === 0}
					onCheckedChange={(v) =>
						setSelected(
							v === true ? new Set(group.actionable_issue_ids) : new Set(),
						)
					}
					aria-label="Select all actionable pages in this group"
				/>
				<button
					type="button"
					onClick={() => setExpanded((v) => !v)}
					aria-expanded={expanded}
					className="flex min-w-0 flex-1 items-center gap-2 text-left hover:bg-muted"
				>
				{expanded ? (
					<ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
				) : (
					<ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
				)}
				<Copy className="h-4 w-4 shrink-0 text-coral" />
				<Badge
					variant="outline"
					className="shrink-0 bg-coral-light text-coral-dark border-coral/40"
				>
					{TYPE_LABELS[group.issue_type]}
				</Badge>
				<span
					className="min-w-0 flex-1 truncate font-mono text-sm text-foreground"
					title={group.shared_value ?? undefined}
				>
					&ldquo;{group.shared_value}&rdquo;
				</span>
				<span className="shrink-0 text-xs text-muted-foreground">
					{group.member_count} pages
				</span>
				{allApplied ? (
					<Badge
						variant="outline"
						className="shrink-0 bg-teal-light text-teal-dark border-teal/40"
					>
						<CheckCircle2 className="mr-1 h-3 w-3" />
						Applied
					</Badge>
				) : anyGenerating ? (
					<Badge
						variant="outline"
						className="shrink-0 bg-teal-light text-teal-dark border-teal/40"
					>
						<Loader2 className="mr-1 h-3 w-3 animate-spin" />
						Generating
					</Badge>
				) : appliedCount > 0 ? (
					<Badge
						variant="outline"
						className="shrink-0 bg-teal-light text-teal-dark border-teal/40"
					>
						{appliedCount}/{group.issues.length} applied
					</Badge>
				) : suggestionsReady > 0 ? (
					<Badge
						variant="outline"
						className="shrink-0 bg-coral-light text-coral-dark border-coral/40"
					>
						{suggestionsReady} suggestion{suggestionsReady === 1 ? "" : "s"}
					</Badge>
				) : null}
				</button>
			</div>

			{expanded && group.global_template_h1 && (
				<div className="border-t bg-coral-light/60 px-4 py-3">
					<p className="text-sm text-coral-dark">
						<span className="font-semibold">
							This H1 comes from a shared theme/template element
						</span>{" "}
						— it isn&apos;t in the page bodies, so per-page applies can&apos;t
						fix it ({group.global_template_evidence_count} attempts hit the
						builder/theme hand-off). One theme edit (e.g. a dynamic post-title
						heading) fixes all {group.member_count} pages at once.
					</p>
					{group.issues.some((i) => i.fix_status === "manual") ? (
						<p className="mt-2 flex items-center gap-1.5 text-sm text-teal-dark">
							<LifeBuoy className="h-4 w-4" />
							Expert fix requested for this group.
						</p>
					) : (
						<Button
							type="button"
							size="sm"
							className="mt-2 gap-1.5 bg-coral text-white hover:bg-coral-hover"
							disabled={requestManualFix.isPending}
							onClick={() => {
								const rep = group.issues[0];
								const sampleUrls = group.issues
									.slice(0, 5)
									.map((i) => i.page_url)
									.join("\n");
								requestManualFix.mutate({
									issueId: String(rep.id),
									note:
										`GLOBAL-TEMPLATE H1 (one ticket for the whole group): the shared heading ` +
										`"${group.shared_value}" appears on ${group.member_count} pages and is rendered ` +
										`by a shared theme/layout element (per-page applies hit the builder/theme ` +
										`hand-off). One theme edit — e.g. make the element render the post title ` +
										`dynamically — fixes all pages at once. Sample pages:\n${sampleUrls}`,
								});
							}}
						>
							{requestManualFix.isPending ? (
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
							) : (
								<LifeBuoy className="h-3.5 w-3.5" />
							)}
							Request expert fix — one ticket for this group
						</Button>
					)}
				</div>
			)}
			{expanded && (
				<>
					<div className="flex flex-wrap items-center justify-end gap-2 border-t px-4 py-2.5">
						{anyGenerating ? (
							// Generation in flight: no re-queue — surface the live monitor.
							<Button
								type="button"
								variant="outline"
								size="sm"
								className="gap-1.5"
								onClick={() => setProgressOpen(true)}
							>
								<Loader2 className="h-3.5 w-3.5 animate-spin" />
								Generating… — view progress
							</Button>
						) : needGeneration.length > 0 ? (
							<Button
								type="button"
								variant="outline"
								size="sm"
								className={`${generate.isPending ? "" : "animate-ai-suggest"} gap-1.5`}
								disabled={generate.isPending}
								onClick={() => startGeneration(needGeneration, false)}
							>
								{generate.isPending ? (
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
								) : (
									<Sparkles className="h-3.5 w-3.5" />
								)}
								Generate suggestions ({needGeneration.length})
							</Button>
						) : (
							// Everything selected already has a suggestion — offer a
							// regenerate (approved rows stay untouched server-side).
							<Tooltip
								title={
									regenerable.length === 0
										? "Nothing selected has a suggestion to regenerate"
										: regenApproved.length > 0
											? `Includes ${regenApproved.length} approved — you'll be asked to confirm discarding those approvals`
											: undefined
								}
							>
								<Button
									type="button"
									variant="outline"
									size="sm"
									className="gap-1.5"
									disabled={regenerable.length === 0 || generate.isPending}
									onClick={() => {
										if (regenApproved.length > 0) {
											setRegenConfirmOpen(true);
										} else {
											startGeneration(regenPending, true);
										}
									}}
								>
									{generate.isPending ? (
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
									) : (
										<RotateCw className="h-3.5 w-3.5" />
									)}
									Regenerate suggestions
									{regenerable.length > 0 && ` (${regenerable.length})`}
								</Button>
							</Tooltip>
						)}
						<Button
							type="button"
							variant="outline"
							size="sm"
							className="gap-1.5"
							disabled={readyToApprove.length === 0 || approve.isPending}
							onClick={() =>
								approve.mutate({
									issueIds: readyToApprove.map((i) => String(i.id)),
									action: "approve",
								})
							}
						>
							{approve.isPending ? (
								<>
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
									Approving…
								</>
							) : (
								<>
									<CheckCircle2 className="h-3.5 w-3.5" />
									Approve
									{readyToApprove.length > 0 &&
										` (${readyToApprove.length})`}
								</>
							)}
						</Button>
						<Button
							type="button"
							size="sm"
							className="gap-1.5 bg-coral text-white hover:bg-coral-hover"
							disabled={
								!applyInFlight &&
								(readyToApply.length === 0 || applySelected.isPending)
							}
							onClick={() =>
								setApplyStep(applyInFlight ? "applying" : "confirm")
							}
						>
							{applyInFlight || applySelected.isPending ? (
								<>
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
									Applying… — view progress
								</>
							) : (
								<>
									<UploadCloud className="h-3.5 w-3.5" />
									Apply selected
									{readyToApply.length > 0 && ` (${readyToApply.length})`}
								</>
							)}
						</Button>
					</div>
					<ul className="divide-y border-t">
						{group.issues
							.filter((i) => matchesFilter(i, statusFilter))
							.map((issue) => {
							const id = String(issue.id);
							const terminal = TERMINAL.has(issue.fix_status);
							const state = memberState(issue, optimisticSet);
							return (
								<li
									key={id}
									className={cn(
										"flex items-center gap-3 px-4 py-2.5",
										terminal && "opacity-60",
									)}
								>
									<Checkbox
										checked={selected.has(id)}
										disabled={terminal}
										onCheckedChange={(v) => toggle(id, v === true)}
										aria-label={`Select ${issue.page_url}`}
									/>
									<button
										type="button"
										onClick={() => onIssueClick(issue)}
										className="min-w-0 flex-1 text-left"
										title="Open to review the suggestion side by side"
									>
										<span className="block truncate font-mono text-sm text-foreground hover:text-coral-dark hover:underline">
											{issue.page_url}
										</span>
										{/* Why it failed, inline. A bare "Generation failed"
										    tells the user nothing and sent us hunting API
										    keys during a provider outage (2026-07-08). */}
										{state.label === "Generation failed" &&
											generationError(issue) && (
												<span className="mt-0.5 block truncate text-xs text-destructive">
													{generationError(issue)}
												</span>
											)}
									</button>
									{/* Inline retry on a failed generation — no need to hunt
									    for the monitor modal (Jacob QA round 4). */}
									{issue.generation_status === "failed" &&
										!hasSuggestion(issue) &&
										!state.generating && (
											<>
												<Button
													type="button"
													variant="outline"
													size="sm"
													className="h-6 shrink-0 gap-1 px-2 text-xs"
													disabled={generate.isPending}
													onClick={() => startGeneration([issue], false)}
												>
													<RotateCw className="h-3 w-3" />
													Retry
												</Button>
												{/* Repeated failures: retrying is no longer a
												    plan — hand it to an AgileCo expert with the
												    generation error attached (Jacob QA round 6). */}
												{generationAttempts(issue) >=
													EXPERT_ESCALATION_ATTEMPTS && (
													<Tooltip title="Several generation attempts failed — file a ticket so an AgileCo expert can fix this by hand">
														<Button
															type="button"
															size="sm"
															className="h-6 shrink-0 gap-1 bg-coral px-2 text-xs text-white hover:bg-coral-hover"
															disabled={requestManualFix.isPending}
															onClick={() =>
																requestManualFix.mutate(String(issue.id))
															}
														>
															<LifeBuoy className="h-3 w-3" />
															Expert help
														</Button>
													</Tooltip>
												)}
											</>
										)}
									{/* The state chip is a review shortcut too — clicking it
									    opens the same drawer as the URL. */}
									<Tooltip
										title={
											state.label === "Generation failed"
												? generationError(issue) ||
													"Generation failed — open the issue for details"
												: "Open the issue to review or edit its suggested fix"
										}
									>
										<button
											type="button"
											onClick={() => onIssueClick(issue)}
											aria-label={`Review ${issue.page_url}`}
											className="shrink-0"
										>
											<Badge
												variant="outline"
												className={cn("cursor-pointer", state.cls)}
											>
												{state.generating && (
													<Loader2 className="mr-1 h-3 w-3 animate-spin" />
												)}
												{state.label}
											</Badge>
										</button>
									</Tooltip>
								</li>
								);
							})}
					</ul>
				</>
			)}

			<Modal
				open={regenConfirmOpen}
				onCancel={() => setRegenConfirmOpen(false)}
				footer={null}
				title={`Regenerate ${regenerable.length} suggestion${regenerable.length === 1 ? "" : "s"}?`}
			>
				<div className="space-y-4">
					<p className="text-sm text-foreground">
						{regenApproved.length} of these{" "}
						{regenApproved.length === 1 ? "is" : "are"} already approved —
						regenerating discards{" "}
						{regenApproved.length === 1 ? "that approval" : "those approvals"}{" "}
						and moves them back to pending review.
					</p>
					<div className="flex justify-end gap-2">
						<Button
							type="button"
							variant="outline"
							onClick={() => setRegenConfirmOpen(false)}
						>
							Cancel
						</Button>
						<Button
							type="button"
							className="gap-1.5 bg-destructive text-white hover:bg-destructive/90"
							onClick={() => {
								setRegenConfirmOpen(false);
								startGeneration(regenerable, true, true);
							}}
						>
							<RotateCw className="h-4 w-4" />
							Discard approvals &amp; regenerate
						</Button>
					</div>
				</div>
			</Modal>
			<GenerationProgressModal
				open={progressOpen}
				onClose={() => setProgressOpen(false)}
				group={group}
				optimisticQueued={optimisticSet}
				onRetry={(issueId) => {
					const issue = byId.get(issueId);
					if (issue) startGeneration([issue], false);
				}}
				retryPending={generate.isPending}
			/>
			<ApplyProgressModal
				step={applyStep}
				count={applyTargetIds.length || readyToApply.length}
				result={applyResult}
				targets={applyTargetIds
					.map((id) => byId.get(id))
					.filter((i): i is PageIssue => Boolean(i))}
				onConfirm={runApply}
				onCollapse={() => setApplyStep("background")}
				onCancelWait={() => {
					setApplyStep("closed");
					setApplyTargetIds([]);
				}}
				onClose={() => {
					setApplyStep("closed");
					setApplyResult(null);
					setApplyTargetIds([]);
				}}
			/>
		</div>
	);
}

/** Panel-level batch apply: every APPROVED member across all groups. */
function ApplyAllGroups({
	runId,
	groups,
}: {
	runId: string;
	groups: DuplicateGroup[];
}) {
	const [step, setStep] = useState<ApplyStep>("closed");
	const [result, setResult] = useState<ApplyFixesResponse | null>(null);
	const [targetIds, setTargetIds] = useState<string[]>([]);
	const applySelected = useApplySelectedFixes();
	const queryClient = useQueryClient();

	const allIssues = useMemo(
		() => groups.flatMap((g) => g.issues),
		[groups],
	);
	const byId = useMemo(() => {
		const m = new Map<string, PageIssue>();
		for (const i of allIssues) m.set(String(i.id), i);
		return m;
	}, [allIssues]);
	const approved = allIssues.filter((i) => i.fix_status === "approved");

	const inFlight = step === "applying" || step === "background" || step === "lost";
	useEffect(() => {
		if (!inFlight) return;
		const timer = window.setInterval(() => {
			queryClient.invalidateQueries({
				queryKey: ["technical-audit", "duplicate-groups"],
			});
		}, 10_000);
		return () => window.clearInterval(timer);
	}, [inFlight, queryClient]);

	const run = () => {
		const ids = approved.map((i) => String(i.id));
		setTargetIds(ids);
		setStep("applying");
		applySelected
			.mutateAsync({ runId, issueIds: ids })
			.then((res) => {
				setResult(res);
				setStep("done");
			})
			.catch(() => setStep("lost"));
	};

	if (approved.length === 0 && !inFlight && step !== "done") return null;
	return (
		<>
			<Button
				type="button"
				size="sm"
				className="gap-1.5 bg-coral text-white hover:bg-coral-hover"
				disabled={!inFlight && approved.length === 0}
				onClick={() => setStep(inFlight ? "applying" : "confirm")}
			>
				{inFlight ? (
					<>
						<Loader2 className="h-3.5 w-3.5 animate-spin" />
						Applying… — view progress
					</>
				) : (
					<>
						<UploadCloud className="h-3.5 w-3.5" />
						Apply approved — all groups ({approved.length})
					</>
				)}
			</Button>
			<ApplyProgressModal
				step={step}
				count={targetIds.length || approved.length}
				result={result}
				targets={targetIds
					.map((id) => byId.get(id))
					.filter((i): i is PageIssue => Boolean(i))}
				onConfirm={run}
				onCollapse={() => setStep("background")}
				onCancelWait={() => {
					setStep("closed");
					setTargetIds([]);
				}}
				onClose={() => {
					setStep("closed");
					setResult(null);
					setTargetIds([]);
				}}
			/>
		</>
	);
}

interface DuplicateGroupsPanelProps {
	runId: string | null;
	onIssueClick: (issue: PageIssue) => void;
}

/**
 * Consolidated duplicate review (BDD A5): each card is one group of pages
 * sharing the same title / meta description / H1. Cards open collapsed (58
 * groups must scan fast); expand one, select members, bulk-generate distinct
 * suggestions (with a live progress monitor), review via the normal issue
 * drawer, then "Apply selected" writes the approved fixes to WordPress in one
 * batch — each with its own history / revert.
 */
export function DuplicateGroupsPanel({
	runId,
	onIssueClick,
}: DuplicateGroupsPanelProps) {
	const { data, isLoading, error } = useDuplicateGroups(runId);
	// Default "all": with "todo" as the default, a group VANISHES the moment you
	// finish applying it — while the results modal is still open. Applied work
	// stays put; the filter is there when you want to focus.
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

	const counts = useMemo(() => {
		const all = (data?.groups ?? []).flatMap((g) => g.issues);
		const applied = all.filter((i) => APPLIED.has(i.fix_status)).length;
		return { applied, todo: all.length - applied, all: all.length };
	}, [data]);

	// A group is worth showing only if it still has members in the current slice.
	const visibleGroups = useMemo(
		() =>
			(data?.groups ?? []).filter((g) =>
				g.issues.some((i) => matchesFilter(i, statusFilter)),
			),
		[data, statusFilter],
	);

	if (isLoading) {
		return (
			<div className="flex h-48 items-center justify-center">
				<Spin />
			</div>
		);
	}
	if (error) {
		return (
			<div className="rounded-xl border bg-card p-8 text-center text-sm text-destructive">
				Failed to load duplicate groups.
			</div>
		);
	}
	if (!data || data.group_count === 0) {
		return (
			<div className="rounded-xl border bg-card px-8 py-16 text-center">
				<Copy className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
				<h3 className="text-lg font-semibold text-foreground">
					No duplicate groups
				</h3>
				<p className="mt-1 text-sm text-muted-foreground">
					This run has no pages sharing the same title, meta description, or
					H1 — nothing to review in bulk.
				</p>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="text-sm text-muted-foreground">
					{data.total_issues} duplicate issues consolidated into{" "}
					{data.group_count} groups. Expand a group, deselect any page that
					should keep the shared value, generate distinct suggestions for the
					rest, review, then apply in one batch.
				</p>
				<ApplyAllGroups runId={String(runId)} groups={data.groups} />
			</div>
			{/* Applied work stays inside its group rather than in a separate tab —
			    a fixed page only makes sense next to the duplicates it was fixed
			    against (Jacob QA: "there should be a group of applied"). */}
			<div className="flex flex-wrap items-center gap-1 rounded-lg border bg-card p-1 text-sm">
				{(
					[
						["all", `All (${counts.all})`],
						["todo", `Needs work (${counts.todo})`],
						["applied", `Applied (${counts.applied})`],
					] as [StatusFilter, string][]
				).map(([key, label]) => (
					<button
						key={key}
						type="button"
						onClick={() => setStatusFilter(key)}
						className={cn(
							"rounded-md px-3 py-1.5 font-medium transition-colors",
							statusFilter === key
								? "bg-coral text-white"
								: "text-muted-foreground hover:bg-muted",
						)}
					>
						{label}
					</button>
				))}
			</div>
			{visibleGroups.length === 0 ? (
				<div className="rounded-xl border bg-card px-8 py-12 text-center text-sm text-muted-foreground">
					{statusFilter === "applied"
						? "Nothing applied yet. Fixes you apply will collect here."
						: "Every duplicate in this run has been handled."}
				</div>
			) : (
				visibleGroups.map((group) => (
					<GroupCard
						key={`${group.issue_type}:${group.shared_value}`}
						group={group}
						runId={String(runId)}
						statusFilter={statusFilter}
						onIssueClick={onIssueClick}
					/>
				))
			)}
		</div>
	);
}
